/*============================================================================
  HC - IB desktop menu sheet - 2026-09-30

  What the desktop mega menus drawn as a sheet share: the BRANDS menu
  (assets/ib-brands-menu.js) and the department menus such as HAIR
  (assets/ib-category-menu.js). Loaded from layout/theme.liquid before them,
  whenever either is switched on. Each menu makes one sheet for its root and
  keeps only what is its own (the brands' search and letters, the
  departments' columns); the sheet does the rest, the same way for all.

  theme.js still opens and closes each menu: the tab is a stock
  [data-mega-menu-trigger], and theme.headerNav toggles .is-focused on its
  <li> (click, click outside, Escape). The sheet watches that class and:
  - Tells the menu: hydrate() (also as the pointer or focus reaches the tab,
    so pictures are on their way before the click), measure() and opened()
    on opening, reset() after the fade-out on closing, when the list also
    goes back to the top.
  - Holds the page still while open: the wheel and touch move only the
    menu's list or a drawer over the page (a pinch or Ctrl+wheel zooms, and
    is left alone), and the wheel over the rest of the sheet moves the list.
    <html> carries the menu's class meanwhile (pageClass), which hides the
    product pages' sticky buy bars.
  - Closes it through the tab, so theme.js does its own bookkeeping: from
    the menu's close buttons, or when focus tabs out, the window narrows past
    the desktop layout, the header compresses under it, a popup or the cart
    opens over the page, or Back/Forward restores the page. Closing with
    focus inside puts focus back on the tab.
  - Hands the menu the keys that are its to take (keydown()): pressed inside
    it (its tab included) or on the page itself, while open. scrollKey()
    moves the list for the scrolling keys, and reveal() focuses a link and
    scrolls the list, never the page, to show it.
  - Owns every listener, the menu's own included (listen()), so destroy()
    takes them all.

  window.IBMenuSheet = { create, scrollsInside, wheelPixels }. ES5 on purpose
  (no build step).
==============================================================================*/
(function () {
  'use strict';

  var SELECTORS = {
    item: '.site-nav__item',
    trigger: '[data-mega-menu-trigger]'
  };

  var OPEN_CLASS = 'is-focused'; // theme.headerNav's open state on the nav <li>
  // Resetting waits for the sheet's fade-out, so it doesn't reflow while it
  // is still visible.
  var RESET_DELAY_MS = 250;
  // What an arrow key, or a wheel's line, scrolls the list by (a page is the
  // list's height less this).
  var LINE_PX = 40;

  // Every sheet on the page: a menu's <html> class stays while any sheet
  // with that class is open.
  var sheets = [];

  function closest(el, selector) {
    if (!el || el.nodeType !== 1) return null;
    if (typeof el.closest === 'function') return el.closest(selector);
    for (var n = el; n && n.nodeType === 1; n = n.parentNode) {
      if (n.matches && n.matches(selector)) return n;
    }
    return null;
  }

  function focusQuietly(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  /*
    Whether a wheel (dy, its direction) or a touch (dy 0: does anything
    scroll at all) over el would move something short of the page itself:
    the menu's list, or anything else scrollable over the page, such as the
    stock cart drawer when cart_drawer_v2 is off. The page itself - body and
    <html> - doesn't count: that is what the open menu holds still.
  */
  function scrollsInside(el, dy) {
    var page = document.documentElement;
    for (var n = el; n && n.nodeType === 1 && n !== document.body && n !== page; n = n.parentNode) {
      var room = (n.scrollHeight || 0) - (n.clientHeight || 0);
      if (room <= 1) continue;
      var style = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(n) : null;
      var overflow = style ? style.overflowY : 'auto';
      if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'overlay') continue;
      var top = n.scrollTop || 0;
      if (!dy || (dy > 0 ? top < room - 1 : top > 0)) return true;
    }
    return false;
  }

  // A wheel's vertical movement in pixels. Some browsers count a mouse
  // wheel's notches in lines, or pages.
  function wheelPixels(evt, list) {
    var dy = evt.deltaY || 0;
    if (evt.deltaMode === 1) return dy * LINE_PX;
    if (evt.deltaMode === 2) return dy * Math.max(LINE_PX, ((list && list.clientHeight) || 0) - LINE_PX);
    return dy;
  }

  /*
    menu: the controller, with any of hydrate(), measure(), opened(),
    reset(), resized() and keydown(evt, key).
    options: root (the menu's dropdown), pageClass (for <html>), scroller
    (the list), closers (buttons that close it), links (the selector of the
    links the arrow keys move between, which scrollKey leaves them).
  */
  function Sheet(menu, options) {
    this.menu = menu;
    this.root = options.root;
    this.item = closest(this.root, SELECTORS.item) || this.root.parentNode;
    this.trigger = this.item ? this.item.querySelector(SELECTORS.trigger) : null;
    this.scroller = options.scroller || null;
    this.pageClass = options.pageClass;
    this.links = options.links || '';

    this.isOpen = false;
    this.resetTimer = null;
    this.listeners = [];
    this.observer = null;

    this.bind(options.closers || []);
    sheets.push(this);
  }

  Sheet.prototype = {
    listen: function (target, type, fn, opts) {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, fn, opts || false);
      this.listeners.push([target, type, fn, opts || false]);
    },

    destroy: function () {
      for (var i = 0; i < this.listeners.length; i++) {
        var l = this.listeners[i];
        l[0].removeEventListener(l[1], l[2], l[3]);
      }
      this.listeners = [];
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      var at = sheets.indexOf(this);
      if (at !== -1) sheets.splice(at, 1);
      if (this.isOpen) this.holdPage(false);
      clearTimeout(this.resetTimer);
    },

    call: function (name, a, b) {
      if (this.menu && typeof this.menu[name] === 'function') return this.menu[name](a, b);
    },

    /* ---------------- open / close ---------------- */

    // Catches up with the tab's class. The menu calls it once it is set up.
    sync: function () {
      var open = Boolean(this.item && this.item.classList.contains(OPEN_CLASS));
      if (open === this.isOpen) return;
      if (open) this.onOpen();
      else this.onClose();
    },

    onOpen: function () {
      this.isOpen = true;
      clearTimeout(this.resetTimer);
      this.holdPage(true);
      this.call('hydrate');
      this.call('measure');
      this.call('opened');
    },

    onClose: function () {
      var self = this;
      this.isOpen = false;
      this.holdPage(false);
      var active = document.activeElement;
      if (active && active !== this.trigger && this.root.contains(active)) focusQuietly(this.trigger);
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(function () {
        if (self.isOpen) return;
        self.call('reset');
        if (self.scroller) self.scroller.scrollTop = 0;
      }, RESET_DELAY_MS);
    },

    // Closes through the tab so theme.js does its own bookkeeping
    // (aria-expanded, its open flags). If theme.js never bound it, close here.
    close: function () {
      if (!this.item || !this.item.classList.contains(OPEN_CLASS)) return;
      if (this.trigger && typeof this.trigger.click === 'function') this.trigger.click();
      if (this.item.classList.contains(OPEN_CLASS)) {
        this.item.classList.remove(OPEN_CLASS);
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      }
      this.sync();
    },

    /*
      While a menu is open the page behind it stays put, wherever the wheel
      turns: over the header too, where a scroll would compress it and hide
      the nav row the sheet hangs from. See stopScroll (bind). On the document
      only while open, because a wheel listener that can cancel makes every
      scroll wait on it. And <html> gets the menu's class, for as long as any
      sheet with it is open: going from one department straight to another,
      the one opening may hear of it before the one closing.
    */
    holdPage: function (on) {
      var method = on ? 'addEventListener' : 'removeEventListener';
      document[method]('wheel', this.stopScroll, { passive: false });
      document[method]('touchmove', this.stopScroll, { passive: false });
      var open = on;
      for (var i = 0; i < sheets.length; i++) {
        if (sheets[i] !== this && sheets[i].isOpen && sheets[i].pageClass === this.pageClass) open = true;
      }
      var html = document.documentElement;
      if (html && html.classList && this.pageClass) html.classList.toggle(this.pageClass, open);
    },

    /* ---------------- keyboard ---------------- */

    // Focuses a link and scrolls the list to show it, the page never: the
    // list sits inside the window, so nothing past it needs to move.
    reveal: function (el) {
      if (!el) return;
      focusQuietly(el);
      if (el !== this.trigger && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    },

    /*
      The page behind the sheet doesn't scroll, and the list only scrolls by
      itself with focus inside it, so a scrolling key anywhere else in the
      menu scrolls the list: PageUp/PageDown from anywhere, a text field
      included (field); outside a text field, the Up and Down arrows (a
      link's go to the next link instead), Home/End, and Space on anything
      but a button, which Space presses. Returns whether it took the key.
    */
    scrollKey: function (evt, key, field) {
      var list = this.scroller;
      var target = evt.target;
      if (!list) return false;
      var page = Math.max(LINE_PX, (list.clientHeight || 0) - LINE_PX);
      var top = list.scrollTop || 0;
      var to = null;
      if (key === 'PageDown') to = top + page;
      else if (key === 'PageUp') to = top - page;
      else if (field) return false;
      else if (key === 'ArrowDown' || key === 'ArrowUp') {
        if (this.links && closest(target, this.links)) return false;
        to = top + (key === 'ArrowDown' ? LINE_PX : -LINE_PX);
      } else if (key === 'Home') to = 0;
      else if (key === 'End') to = list.scrollHeight || 0;
      else if (key === ' ' || key === 'Spacebar') {
        if (closest(target, 'button, [role="button"]')) return false;
        to = top + (evt.shiftKey ? -page : page);
      }
      if (to === null) return false;
      evt.preventDefault();
      list.scrollTop = Math.max(0, to);
      return true;
    },

    // Whether a key is the menu's to take: pressed inside it (its tab
    // included), or on the page itself, where focus goes after a click on
    // the sheet's blank space. Not in anything else, such as a popup that
    // opened over the page meanwhile.
    ownsKey: function (target) {
      return !target || target === document.body || target === document.documentElement ||
        Boolean(this.item && this.item.contains(target));
    },

    onDocumentKeydown: function (evt) {
      if (!this.isOpen || evt.defaultPrevented || evt.isComposing) return;
      if (evt.metaKey || evt.ctrlKey || evt.altKey) return;
      if (!this.ownsKey(evt.target)) return;
      this.call('keydown', evt, evt.key || '');
    },

    /* ---------------- wiring ---------------- */

    bind: function (closers) {
      var self = this;

      if (this.item && typeof window.MutationObserver === 'function') {
        this.observer = new window.MutationObserver(function () {
          self.sync();
        });
        this.observer.observe(this.item, { attributes: true, attributeFilter: ['class'] });
      }

      // Pictures start loading as the pointer heads for the tab, so they are
      // usually there by the time the click lands.
      this.listen(this.trigger, 'pointerenter', function () {
        self.call('hydrate');
      });
      this.listen(this.trigger, 'focus', function () {
        self.call('hydrate');
      });

      for (var i = 0; i < closers.length; i++) {
        this.listen(closers[i], 'click', function (evt) {
          evt.preventDefault();
          self.close();
        });
      }

      // While open (holdPage) the wheel and touch scroll only what scrolls
      // over the page: the list (overscroll-behavior keeps it from handing
      // the scroll on to the page at its ends), or a drawer above it. Over
      // the rest of the sheet (its toolbar, the dimmed page) the wheel
      // scrolls the list, as the scrolling keys do.
      this.stopScroll = function (evt) {
        if (evt.ctrlKey || (evt.touches && evt.touches.length > 1)) return;
        var dy = evt.type === 'wheel' ? wheelPixels(evt, self.scroller) : 0;
        if (scrollsInside(evt.target, dy)) return;
        if (evt.cancelable) evt.preventDefault();
        var list = self.scroller;
        if (dy && list && self.root.contains(evt.target) && !list.contains(evt.target)) {
          list.scrollTop = Math.max(0, (list.scrollTop || 0) + dy);
        }
      };

      this.listen(document, 'keydown', function (evt) {
        self.onDocumentKeydown(evt);
      });

      // Tabbing out of the menu closes it, like clicking outside does. Only
      // when focus lands somewhere real: a click on nothing blurs to null.
      this.listen(this.item, 'focusout', function (evt) {
        var next = evt.relatedTarget;
        if (self.isOpen && next && next.nodeType === 1 && !self.item.contains(next)) self.close();
      });

      // Narrowed below the desktop layout, the nav and the sheet go (CSS);
      // the menu would stay open unseen, holding the page and taking keys:
      // close it. Otherwise the sheet is measured again.
      this.listen(window, 'resize', function () {
        if (!self.isOpen) return;
        var rects = typeof self.root.getClientRects === 'function' ? self.root.getClientRects() : null;
        if (rects && !rects.length) {
          self.close();
          return;
        }
        self.call('measure');
        self.call('resized');
      });
      // The header changes shape only when the page scrolls under it, which
      // holdPage stops the wheel doing but not a scrollbar drag. Compressing,
      // it hides the nav row the sheet hangs from, and the menu would stay
      // open unseen, still taking keys. Close it.
      this.listen(document, 'headerStickyChange', function () {
        if (self.isOpen) self.close();
      });
      // A popup or the cart opening over the page (the timed login popup,
      // quick shop, the cart drawer) takes over, as it does from the mobile
      // menu: left open under it, the menu would still hold the page and
      // take keys. The popup has focus by now, and closing leaves focus
      // outside the menu where it is.
      this.listen(document, 'modalOpen', function () {
        if (self.isOpen) self.close();
      });
      this.listen(document, 'CartDrawer:open', function () {
        if (self.isOpen) self.close();
      });

      // Back/Forward can restore this page from memory with the menu open.
      this.listen(window, 'pageshow', function (evt) {
        if (evt && evt.persisted) self.close();
      });
    }
  };

  window.IBMenuSheet = {
    create: function (menu, options) {
      return new Sheet(menu, options);
    },
    scrollsInside: scrollsInside,
    wheelPixels: wheelPixels
  };
})();
